import { PostHog, useFeatureFlagEnabled, useFeatureFlagVariantKey } from "posthog-js/react";
import { bridge } from "./bridge";

// First build number that ships the flutter_stripe Payment Sheet integration.
// Any native app build below this falls back to RevenueCat IAP regardless of
// the experiment flag.
const STRIPE_NATIVE_MIN_BUILD = 109;

export const forceParameterFeatureFlags = (posthog: PostHog) => {
  const params = new URLSearchParams(window.location.search);
  const ffParam = params.get("ff");

  if (!ffParam) return;

  // Parse the feature flag overrides
  // Format: flag1:value1,flag2:value2
  const overrides: Record<string, string | boolean> = {};

  ffParam.split(",").forEach((pair) => {
    const [flag, value] = pair.split(":");
    if (flag && value !== undefined) {
      // Convert "true"/"false" strings to booleans
      if (value === "true") {
        overrides[flag.trim()] = true;
      } else if (value === "false") {
        overrides[flag.trim()] = false;
      } else {
        overrides[flag.trim()] = value.trim();
      }
    }
  });

  if (Object.keys(overrides).length > 0) {
    posthog.featureFlags.overrideFeatureFlags({ flags: overrides });
  }
}
// Feature flag hooks
export const useQuizInterstitials = () => {
  const variant = useFeatureFlagVariantKey("quiz_interstitials");

  return variant === "experiment";
};

export const useShowReferralSection = () => useFeatureFlagEnabled("show_referral_section");

// "+1" invite feature — gates the booking-card roster, the /invite/:code
// accept page, and the post-signin redirect that picks up stashed codes.
// PostHog flag key: show_plus_one_invites.
export const usePlusOneInvites = () => useFeatureFlagEnabled("plus_one");

export const useLandingCRO2 = () => useFeatureFlagVariantKey("landing_cro2") === "experiment";

export const useQuizScanningScreen = () => useFeatureFlagVariantKey("quiz_scanning_screen") == "experiment";

export const useQuizPhoneOptional = () => {
  const inExperiment = useFeatureFlagVariantKey("quiz_phone_optional") === "experiment";
  return inExperiment && bridge.isNative;
};

export const useQuizV2 = () => {
  const variant = useFeatureFlagVariantKey("quiz_var");
  return variant === "v2" || variant === "v2short";
};

export const useShortQuiz = () => {
  const variant = useFeatureFlagVariantKey("quiz_var");
  return variant === "short" || variant === "v2short";
};

// Checkout v2 redesign — TimeLeft-shaped full-page sell sheet vs the legacy modal.
// PostHog flag key: checkout_v2. Variants: control (legacy modal) | experiment (v2).
export const useCheckoutV2 = () =>
  useFeatureFlagVariantKey("checkout_v2") === "experiment";

// Returns undefined while PostHog flags are still loading, then a boolean.
export const useMinimalLanding = (): boolean | undefined => {
  const variant = useFeatureFlagVariantKey("minimal_landing");
  if (variant === undefined) return undefined;
  return variant === "experiment";
};

// Native Stripe checkout — replaces RevenueCat IAP with flutter_stripe Payment Sheet.
// Both conditions must hold:
//   1. PostHog flag `native_stripe_checkout` is in the `experiment` variant
//   2. The native build number is at least STRIPE_NATIVE_MIN_BUILD (older builds
//      don't have the StripeService wired up and would silently fail)
// Returns false when running on web (no bridge → buildNumber is 0).
export const useNativeStripeEnabled = (): boolean => {
  const inExperiment = useFeatureFlagVariantKey("native_stripe_checkout") === "experiment";
  return inExperiment && bridge.appBuildNumber >= STRIPE_NATIVE_MIN_BUILD;
};

// Host-created custom events — gates the entire host surface (create page,
// "my events" links, anything else added later). Public /events/{slug} pages stay
// ungated so share links keep working for recipients without the flag.
// Returns undefined while flags are loading, then a boolean.
export const useCustomEvents = (): boolean | undefined =>
  useFeatureFlagEnabled("custom_events");
